import { useEffect, useRef } from 'react';

import { RichText } from '#/components/ui/RichText';
import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { useFocusTrap } from '#/hooks/useFocusTrap';
import { cn } from '#/utils/cn';

const TITLE_ID = 'definition-drawer-title';

const ARROW_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M15.7063 8.70625C16.0969 8.31563 16.0969 7.68125 15.7063 7.29063L11.7063 3.29063C11.3156 2.9 10.6812 2.9 10.2906 3.29063C9.9 3.68125 9.9 4.31563 10.2906 4.70625L12.5844 7H1C0.446875 7 0 7.44688 0 8C0 8.55313 0.446875 9 1 9H12.5844L10.2906 11.2938C9.9 11.6844 9.9 12.3188 10.2906 12.7094C10.6812 13.1 11.3156 13.1 11.7063 12.7094L15.7063 8.70938V8.70625Z"/></svg>';

function withArrowIcon(html: string): string {
  return html.replace(
    /(<a\b[^>]*>)([^<]*?)\s*(?:&gt;|>)\s*(<\/a>)/g,
    (_match, open: string, text: string, close: string) =>
      `${open}${text}${ARROW_ICON_SVG}${close}`,
  );
}

// Fallback display name for a term whose glossary entry has no `name`.
function formatSlug(slug: string) {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DefinitionDrawer() {
  const { activeDefinition, close, open, registeredTerms, termsBySlug } =
    useDefinitionDrawer();
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const isOpen = activeDefinition !== null;

  const otherTerms = registeredTerms.filter((s) => s !== activeDefinition);

  const label = (slug: string) =>
    termsBySlug.get(slug)?.name ?? formatSlug(slug);
  const activeTerm = activeDefinition
    ? termsBySlug.get(activeDefinition)
    : undefined;

  // Restore focus to trigger when drawer closes
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Close on Esc
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // Close on click outside.
  // composedPath() in an attempt at Shadow DOM support
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function onClick(e: MouseEvent) {
      if (drawerRef.current && !e.composedPath().includes(drawerRef.current)) {
        close();
      }
    }
    document.addEventListener('click', onClick, { capture: true });
    return () =>
      document.removeEventListener('click', onClick, { capture: true });
  }, [isOpen, close]);

  useFocusTrap({
    active: isOpen,
    containerRef: drawerRef,
    allowOutsideClick: true,
  });

  return (
    <div
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      inert={!isOpen}
      className={cn(
        'w-drawer-mobile sm:w-drawer-tablet lg:w-drawer fixed top-0 right-0 z-50 h-full overflow-y-auto',
        'bg-gray-10 border-divider-subtle border-l',
        'ease-in-out-strong transition-transform duration-300',
        isOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <div className="flow flow-14 h-full p-32">
        <div className="flex w-full items-center justify-between gap-16">
          <p className="text-fg-strong text-static-12 font-semibold">
            Learn &amp; Define
          </p>
          <button
            type="button"
            aria-label="Hide definition drawer"
            onClick={close}
            data-track="glossary_drawer_hide"
            data-track-label="Hide"
            data-track-context="glossary_drawer"
            className="text-link-fg hover:text-link-fg-hover text-static-12 shrink-0 font-semibold uppercase"
          >
            Hide
          </button>
        </div>

        <h2
          id={TITLE_ID}
          className="text-fg-subheader text-fluid-subheader before:bg-accent relative font-semibold before:absolute before:top-1/2 before:-left-18 before:-mt-6 before:h-12 before:w-12 before:rounded-full before:content-['']"
        >
          {activeDefinition ? label(activeDefinition) : ''}
        </h2>

        {activeDefinition && (
          <div className="flow flow-14 pb-32">
            {activeTerm?.expandedDefinition && (
              <RichText
                html={withArrowIcon(activeTerm.expandedDefinition)}
                className="text-fg-utility text-static-14"
              />
            )}

            {otherTerms.length > 0 && (
              <div className="flow flow-24">
                <hr />
                <section aria-label="Other terms on page">
                  <p className="text-fg-subheader text-fluid-base mb-8 font-semibold">
                    Other terms on this page
                  </p>
                  <div className="flex flex-col items-start">
                    {otherTerms.map((slug) => (
                      <button
                        key={slug}
                        type="button"
                        onClick={() => open(slug)}
                        data-track="glossary_drawer_term"
                        data-track-label={label(slug)}
                        data-track-context="glossary_drawer_related"
                        className="text-link-fg hover:text-link-fg-hover py-4 text-left hover:underline"
                      >
                        {label(slug)}
                      </button>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
